import { toNewRandomizedRounds } from '../../bracket/randomizeMatches'
import { useUpdateGame } from '../../update'
import { useAddGameParticipant } from '../add'
import { type GameFormParticipant, type GameFormGroupParticipant, type GameFormUserParticipant } from '../form'
import { useRemoveGameParticipant } from '../remove'
import { useUpdateGameParticipant } from '../update'
import { toAddRemoveGroupParticipants } from './util'
import {
  type DecoratedGameGroup,
  type DecoratedGameParticipant,
  type GameData,
  type GameParticipantId,
  type GroupId,
  isDefined,
  isEmpty,
  isNotDefined,
  Logger,
  unique,
  type WithGameParticipantId,
} from '@terros/common'
import { useCallback, useMemo } from 'react'

type EditParticipantsParams = {
  game: GameData
  oldParticipants: DecoratedGameParticipant[]
  newParticipants: GameFormParticipant[]
}

type Return = {
  editParticipants: (params: EditParticipantsParams) => Promise<GameFormParticipant[]>
  isBusy: boolean
  error: string | undefined
}

const logger = new Logger('useEditGameParticipants')
// logger.level = 'verbose'

export const useEditGameParticipants = (): Return => {
  const { update: updateGame, isUpdating: isUpdatingGame, error: updateErrorGame } = useUpdateGame()
  const { update, isUpdating, error: updateError } = useUpdateGameParticipant()
  const { add, isAdding, error: addError } = useAddGameParticipant()
  const { remove, isRemoving, error: removeError } = useRemoveGameParticipant()

  const updateGroups = useCallback(
    async (
      game: GameData,
      oldParticipants: DecoratedGameParticipant[],
      newGroups: GameFormGroupParticipant[]
    ): Promise<GameFormGroupParticipant[]> => {
      const gameId = game.gameId
      const oldUserParticipants = oldParticipants.filter((p) => p.type === 'user')
      const oldGroups = oldParticipants.filter((p) => p.type === 'group')
      logger.verbose('updateGroups', JSON.stringify({ newGroups, oldGroups }, null, 2))
      const { usersToRemove, groupsToAdd, groupsToUpdate, groupsToRemove } = toAddRemoveGroupParticipants(
        newGroups,
        oldGroups
      )

      await Promise.all(
        [...oldUserParticipants.map((p) => p.participantId), ...usersToRemove].map((participantId) =>
          remove({ gameId: game.gameId, participantId, archive: false })
        )
      )
      await Promise.all(
        groupsToRemove.map((group) => remove({ gameId: game.gameId, participantId: group, archive: false }))
      )
      const addGroupResult = await add({
        gameId,
        participants: groupsToAdd.map((group) => ({
          users: group.userIds,
          type: 'group',
          groupId: group.participantId,
          name: group.name,
        })),
      })

      await Promise.all(
        groupsToUpdate.map((group) => {
          if (isNotDefined(group.participantId)) return
          return update({ gameId, participantId: group.participantId, name: group.name })
        })
      )
      const newParticipantList: GameFormGroupParticipant[] = newGroups.filter((g) => isDefined(g.participantId))
      const newGroupParticipants: GameFormGroupParticipant[] = addGroupResult.participants
        .filter((p) => p.type === 'group')
        .map(toGameFormGroupParticipant)
      newParticipantList.push(...newGroupParticipants)

      const participantIds: GroupId[] = unique(newParticipantList.map((p) => p.participantId).filter(isDefined))
      await updateRounds(game, participantIds.map(toWithParticipantId))

      return newParticipantList
    },
    []
  )

  const updateUsers = useCallback(
    async (
      game: GameData,
      oldParticipants: DecoratedGameParticipant[],
      userParticipants: GameFormUserParticipant[]
    ): Promise<GameFormUserParticipant[]> => {
      const gameId = game.gameId
      const toAdd = userParticipants.filter(
        (user) => !oldParticipants.some((p) => p.participantId === user.participantId)
      )
      const toRemove = oldParticipants.filter(
        (p) => p.type === 'user' && !userParticipants.some((user) => user.participantId === p.participantId)
      )
      const groupsToRemove = oldParticipants.filter((p) => p.type === 'group')

      logger.verbose('updateUsers', JSON.stringify({ toAdd, toRemove, groupsToRemove }, null, 2))
      await Promise.all(
        groupsToRemove.map((group) => remove({ gameId, participantId: group.participantId, archive: false }))
      )
      await add({
        gameId,
        participants: toAdd.map((user) => ({ type: 'user', userId: user.participantId })),
      })
      await Promise.all(toRemove.map((user) => remove({ gameId, participantId: user.participantId, archive: false })))

      await updateRounds(game, userParticipants)
      return userParticipants
    },
    []
  )

  const updateRounds = useCallback(async (game: GameData, newParticipants: WithGameParticipantId[]) => {
    logger.info('updateRounds called')
    if (game.gameType !== 'bracket') return
    const currentRounds = game.rounds
    if (isEmpty(currentRounds)) return
    if (isDefined(game.settings.seedingType)) return
    const newRounds = toNewRandomizedRounds(currentRounds, newParticipants)
    logger.info('newRounds', JSON.stringify(newRounds, null, 2))
    await updateGame({ ...game, rounds: newRounds })
  }, [])

  const editParticipants = useCallback(
    async ({ game, oldParticipants, newParticipants }: EditParticipantsParams): Promise<GameFormParticipant[]> => {
      if (game.settings.participantType === 'group') {
        return updateGroups(
          game,
          oldParticipants,
          newParticipants.filter((p) => p.type === 'group')
        )
      } else {
        return updateUsers(
          game,
          oldParticipants,
          newParticipants.filter((p) => p.type === 'user')
        )
      }
    },
    [add, remove]
  )

  return useMemo(
    () => ({
      editParticipants,
      isBusy: isAdding || isRemoving || isUpdating || isUpdatingGame,
      error: addError || removeError || updateError || updateErrorGame,
    }),
    [
      editParticipants,
      isAdding,
      isRemoving,
      isUpdating,
      isUpdatingGame,
      addError,
      removeError,
      updateError,
      updateErrorGame,
    ]
  )
}

const toWithParticipantId = (participantId: GameParticipantId): WithGameParticipantId => {
  return { participantId }
}

const toGameFormGroupParticipant = (participant: DecoratedGameGroup): GameFormGroupParticipant => {
  return {
    type: 'group',
    name: participant.name,
    participantId: participant.participantId,
    userIds: participant.users.map((u) => u.participantId),
  }
}
